<i18n>
{
	"en": {
		"theTrip": "the trip",
		"cancelLabel": "Cancel",
		"editTrip": "Edit Trip {number}",
		"newTrip": "Create new trip",
		"save": "Save",
		"saveAndClose": "Save & close",
		"tripNumber": "Trip No.",
		"failedToSave": "Failed to save trip",
		"openTmsR10": "Open trip in TMS R10",
		"openTmsR10Success" : "Request has been sent to TMS R10!",
		"openTmsR10Error" : "Failed sending request to TMS R10. Error message: ",
		"orderAlreadyAdded": "A selected order is already part of the selected trip.",
		"main": "Trip Details",
		"orders": "Orders",
		"deviations": "Deviations",
		"additionalInfo": "Other",
		"validationErrors": "Validation error. To save the trip the errors must be corrected as described in the schema.",
		"tripChangelog": "Changelog",
		"changeLoadingSequence": "Change loading sequence",
		"loadingSequenceChanged": "Loading sequence saved",
		"tripSaved": "The trip was saved"
	},
	"no": {
		"theTrip": "turen",
		"cancelLabel": "Avbryt",
		"editTrip": "Rediger tur {number}",
		"newTrip": "Opprett ny tur",
		"save": "Lagre",
		"saveAndClose": "Lagre & lukk",
		"tripNumber": "Tur Nr.",
		"failedToSave": "Kunne ikke lagre tur",
		"openTmsR10": "Åpne tur i TMS R10",
		"openTmsR10Success" : "En spørring har blitt sendt til TMS R10",
		"openTmsR10Error" : "Spørringen mot TMS R10 feilet med melding: ",
		"orderAlreadyAdded": "Et valgt oppdrag er allerede en del av den valgte turen.",
		"main": "Turdetaljer",
		"orders": "Oppdrag",
		"deviations": "Avvik",
		"additionalInfo": "Annet",
		"validationErrors": "Feil ved validering. For å lagre turen må feilene rettes slik som angitt i skjemaet.",
		"tripChangelog": "Endringer",
		"changeLoadingSequence": "Endre lasterekkefølge",
		"loadingSequenceChanged": "Lasterekkefølge lagret",
		"tripSaved": "Turen ble lagret"
	}
}
</i18n>

<template>
	<t-dialog
		large
		:isShown="isShown"
		:confirmLabel="$t('save')"
		:cancelLabel="$t('cancelLabel')"
		disableCloseOnConfirm
		alwaysRememberFocus
		:hasHeader="false"
		bodyStyle="margin-top: 0px"
		:busy="savePending"
		:keyListeners="getKeyListeners()"
		@closing="checkClose"
		@close="onClose"
	>
		<t-horizontal-panel
			:showRightPanel="editJsonVisible"
		>
			<t-vertical-panel>
				<div slot="header" class="header">
					<t-panel-header
						:text="trip.id ? $t('editTrip', {number: trip.tripNumber}) : $t('newTrip')"
						:icon="trip.id ? 'trip' : 'trip-new'"
						textSize="large"
					/>
					<t-tab-list :selected.sync="selectedTab" :items="tabs" marginBottom="0px">
						<template v-slot="{item, isSelected, index}">
							<t-tab
								:value="item"
								tabindex="-1"
								:selected="isSelected"
							>
								{{$t(item.label)}}

								<template v-if="isTimpexSupport && index === 0 " v-slot:contextMenu>
									<t-menu>
										<t-menu-group>
											<t-menu-item @click="toggleEditJson"><strong>JSON</strong></t-menu-item>
										</t-menu-group>
									</t-menu>
								</template>
							</t-tab>
						</template>
					</t-tab-list>
				</div>

				<component
					:is="selectedTab.component"
					:trip="trip"
					class="new-trip-inner"
				/>
			</t-vertical-panel>

			<template v-slot:right>
				<EditTripJson
					v-show="editJsonVisible"
					:tripId="jsonObjectId"
					style="width: 600px;"
					@hide="hideEditJson"
					@changed="tripJsonChanged"
				/>
			</template>

		</t-horizontal-panel>
		<template #footer>
			<div style="width: 100%; display: flex; justify-content: space-between">
				<div style="display:flex">
					<t-button
						:value="$t('openTmsR10')"
						appearance="primary"
						type="bp"
						iconBefore="globe"
						@click="openTripInTmsR10()"
					/>
					<t-button
						v-if="!isNewTrip"
						:value="$t('tripChangelog')"
						style="margin-left: 15px;"
						type="bp"
						iconBefore="history"
						tabindex="-1"
						@click="toggleTripChangelog(true)"
					/>
					<t-button
						v-if="selectedTab.label === 'orders'"
						style="margin-left: 15px;"
						:value="$t('changeLoadingSequence')"
						@click="setChangeLoadingSequenceVisible(true)"
					/>
				</div>

				<div style="display:flex">
					<t-button
						:value="$t('cancelLabel')"
						appearance="default"
						ointent="default"
						type="bp"
						@click="onCancel"
					/>
					<t-button
						:value="$t('saveAndClose')"
						:appearance="'primary'"
						style="margin-left: 15px;"
						type="bp"
						@click="saveTripAndClose()"
					/>
					<t-button
						:value="$t('save')"
						:appearance="'primary'"
						style="margin-left: 15px;"
						type="bp"
						@click="saveTrip()"
					/>
				</div>
			</div>
		</template>

		<ConfirmUnsavedChanges
			:isShown="confirmUnsavedChanges"
			:subject="$t('theTrip')"
			@cancel="toggleUnsavedChanges(false)"
			@save="saveTripAndClose"
			@close="onCloseDirty"
		/>
		<TripChangelog
			:isShown="tripChangelogVisible"
			:changelog="changelog"
			:legs="trip.legs"
			@close="toggleTripChangelog(false)"
		/>
		<ChangeLoadingSequence
			:isShown="changeLoadingSequenceVisible"
			:trip="tripForChangeLoadingSequence"
			@changeLoadingSequence="changeLoadingSequence"
			@close="setChangeLoadingSequenceVisible(false)"
		/>
	</t-dialog>
</template>

<script>
import ConfirmUnsavedChanges from '@/components/modals/ConfirmUnsavedChanges';
import newTripMain from './newTripMain';
import newTripOrders from './newTripOrders';
import Deviations from './Deviations';
import newTripAdditionalInfo from './newTripAdditionalInfo';
import EditTripJson from '@/modules/editTripJson/EditTripJson.vue';
import TripChangelog from './modals/TripChangelog';
import ChangeLoadingSequence from '@/modules/tripSearch/modals/ChangeLoadingSequence';

import { eventBus, statuses, modulo } from '@/core';
import { mapState, mapActions } from './store/newTrip.store.js';
import { mapGetters as mapLoginGetters } from '../login/store/login.store';

export default {
	name: 'newTrip',
	components: {
		ConfirmUnsavedChanges,
		EditTripJson,
		TripChangelog,
		ChangeLoadingSequence,
	},

	data() {
		let tabs = [
			{ label: 'main', component: newTripMain },
			{ label: 'orders', component: newTripOrders },
			{ label: 'deviations', component: Deviations },
			{ label: 'additionalInfo', component: newTripAdditionalInfo },
		];
		return {
			selectableStatuses: Object.values(statuses.trip),
			isShown: false,
			selectedTemplate: undefined,
			confirmUnsavedChanges: false,
			closeDirty: false,
			selectedTab: tabs[0],
			savePending: false,
			editJsonVisible: false,
			tabs,
			tripChangelogVisible: false,
			changeLoadingSequenceVisible: false,

			// Needed because of inconsistency between trip in newtrip and trip in loadPlanning
			// The structure in loadPlanning is wrong (according to Lars-Erik),
			// but the ChangeLoadingSequence component assumes trip has that wrong structure
			tripForChangeLoadingSequence: {},
		};
	},

	provide() {
		return { saveTrip: this.saveTrip, };
	},

	computed: {
		...mapState([
			'trip',
			'dirty',
			'changelog'
		]),
		...mapLoginGetters(['isTimpexSupport']),
		jsonObjectId() {
			return this.trip.id;
		},
		isNewTrip() {
			return !this.trip || !this.trip.id;
		},
		tripWasSent() {
			return this.trip.wasSent || this.trip.legs.reduce((acc, leg) => acc && leg.wasSent, true);
		},
	},
	async created() {
		if (!this.trip.id) {
			await this.cleanState();
		}
		eventBus.$on('toggleNewTripModal', this.toggleNewTripModal);
		eventBus.$on('setEditTrip', this.setEditTrip);
	},
	beforeDestroy() {
		eventBus.$off('toggleNewTripModal');
		eventBus.$off('setEditTrip');
	},

	methods: {
		...mapActions([
			'saveNewTrip',
			'updateTrip',
			'cleanState',
			'openTmsR10',
			'setEditTrip',
			'updateLoadingSequence',
		]),
		changeLoadingSequence(mapOrderIdToNewLoadingSeqNo) {
			try {
				this.updateLoadingSequence(mapOrderIdToNewLoadingSeqNo);
				this.setChangeLoadingSequenceVisible(false);

				this.$notify({
					type: 'success',
					title: this.$t('loadingSequenceChanged')
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToChangeLoadingSequence')
				});
			}
		},
		setChangeLoadingSequenceVisible(isVisible) {

			this.changeLoadingSequenceVisible = isVisible;

			if (isVisible)
				this.tripForChangeLoadingSequence
					= refactorTripForChangeLoadingSequence(this.trip);

			// This is needed because of inconsistency of the data structure in trip in newTrip compared to loadPlanning
			function refactorTripForChangeLoadingSequence(trip) {

				let newTrip = { ...trip };

				newTrip.orders
					= trip.orders.map(
						order => ({ ...order })
					);

				return newTrip;
			}
		},
		async openTripInTmsR10() {
			try {
				await this.openTmsR10(this.trip);
				this.$notify({
					type: 'success',
					title: this.$t('openTmsR10Success'),
				});
			} catch (err) {
				this.$notify({
					type: 'error',
					title: `${this.$t('openTmsR10Error')}${err.message}`,
				});
			}
		},
		toggleNewTripModal(value) {
			this.isShown = value;
		},
		toggleUnsavedChanges(value) {
			this.confirmUnsavedChanges = value;
		},

		async saveTrip({ validateDrivers=false, closeDialog=false }={}) {
			this.toggleUnsavedChanges(false);
			this.savePending = true;
			let savedSuccessfully = false;
			try {
				this.$notify({ clean: true });
				if (this.trip.id) {
					await this.updateTrip({ validateDrivers, closeDialog });
				} else {
					await this.saveNewTrip({ validateDrivers, closeDialog });
				}
				this.$notify({
					type: 'success',
					title: this.$t('tripSaved')
				});
				savedSuccessfully = true;
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$te(e.message) ? this.$t(e.message) : this.$t('failedToSave'),
				});
			}
			this.savePending = false;
			return savedSuccessfully;
		},
		async saveTripAndClose() {
			if (!await this.saveTrip({ closeDialog: true })) return;
			this.toggleNewTripModal(false);
			this.cleanState();
			this.selectedTab = this.tabs[0];
			eventBus.$emit('refreshTripList');
		},
		onCancel() {
			if (this.dirty && !this.closeDirty) {
				this.toggleUnsavedChanges(true);
				this.closeDirty = false;
			} else {
				this.onClose();
			}
		},
		checkClose(abort) {
			if (this.dirty && !this.closeDirty) {
				abort(true);
				this.toggleUnsavedChanges(true);
			} else {
				abort(false);
			}
			this.closeDirty = false;
		},
		onCloseDirty() {
			this.closeDirty = true;
			this.onClose();
		},
		onClose() {
			this.cleanState();
			this.selectedTab = this.tabs[0];
			this.toggleUnsavedChanges(false);
			this.toggleNewTripModal(false);
			this.hideEditJson();
			this.$notify({ clean: true });
		},

		hideEditJson() {
			this.editJsonVisible = false;
		},
		toggleEditJson() {
			this.editJsonVisible = !this.editJsonVisible;
		},
		tripJsonChanged() {
			this.setEditTrip(this.trip.id);
		},

		getKeyListeners() {
			return [
				{ 	key: 'F3',
					listener: this.onF3,
					modifiers: { shiftKey: undefined, ctrlKey: false, altKey: false, stop: true, prevent: true } },
				{ 	key: 'S',
					listener: this.saveTrip,
					modifiers: { ctrlKey: true, shiftKey: undefined, altKey: false, stop: true, prevent: true } },
				{ 	key: 'Enter',
					listener: this.saveTripAndClose,
					modifiers: { ctrlKey: true, shiftKey: false, altKey: false, stop: true, prevent: true } },
			];
		},
		onF3(event) {
			let tabIndex = this.tabs.indexOf(this.selectedTab);
			event.shiftKey ? tabIndex-- : tabIndex++;
			tabIndex = modulo(tabIndex, this.tabs.length);
			this.selectedTab = this.tabs[tabIndex];
		},

		toggleTripChangelog(value) {
			this.tripChangelogVisible = value;
		},
	},

};
</script>

<style lang="scss" scoped>
	.header {
		display: grid;
		column-gap: 5px;
		grid-auto-flow: column;
		grid-auto-columns: minmax(min-content, max-content);
		align-content: end;
	}
	.new-trip-inner {
		width: 1000px;
		padding: 5px 10px 5px 5px;
	}
</style>
